import React, { useState, useEffect } from 'react';
import {
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  Snackbar,
  LinearProgress,
  CircularProgress,
  Typography,
  Box,
  Card,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as VerifyIcon
} from '@mui/icons-material';
import { saveAs } from 'file-saver';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import { useRole } from '../../hooks/useRole';
import PageLayout from '../../components/Layout/PageLayout';
import { SiteSlump, SiteSlumpBirthPaperQueryModel } from '../../types/siteSlump';
import BackButton from '../../components/BackButton';


const SlumpBirthPaperPage: React.FC = () => {
  const { t } = useTranslation();
  const { canVerify } = useRole();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  const [slumps, setSlumps] = useState<SiteSlump[]>([]);
  const [loading, setLoading] = useState(false);
  const [queryParams, setQueryParams] = useState<SiteSlumpBirthPaperQueryModel>({
    Docket_No: '',
    Contract_No: '',
    Cube_Mark: '',
    Time_Of_Water_Add: '',
    startTime: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endTime: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [totalCount, setTotalCount] = useState(0);
  const [unverifiedDialog, setUnverifiedDialog] = useState({ open: false });
  const [verifyDialog, setVerifyDialog] = useState({ open: false, action: 'verify' as 'verify' | 'unverify' });

  // Contract numbers for dropdown
  const [contractNumbers, setContractNumbers] = useState<string[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);

  // 統計信息狀態
  const [statistics, setStatistics] = useState({
    totalRecords: 0,
    unverifiedRecords: 0,
    slumpTotal: 0,
    concreteTotal: 0,
    reportCount: 0,
    totalBricks: 0,
    bricks28Day: 0,
    bricksAB: 0,
    bricksEF: 0,
    bricksCD: 0,
    otherBricks: 0
  });

  // Fetch contract numbers on component mount
  useEffect(() => {
    const fetchContractNumbers = async () => {
      try {
        setLoadingContracts(true);
        const numbers = await apiService.getContractNumbers();
        setContractNumbers(numbers);
      } catch (error) {
        console.error('Error fetching contract numbers:', error);
        // 即使失敗也繼續，允許用戶自由輸入
      } finally {
        setLoadingContracts(false);
      }
    };

    fetchContractNumbers();
  }, []);

  // 從 URL 參數初始化查詢條件（只在首次加載時執行；不自動查詢，需按搜尋按鈕）
  useEffect(() => {
    if (urlParamsProcessed) return;

    const urlParams: Partial<SiteSlumpBirthPaperQueryModel> = {};

    // 讀取所有可能的 URL 參數
    const docketNo = searchParams.get('Docket_No');
    const contractNo = searchParams.get('Contract_No');
    const startTime = searchParams.get('startTime');
    const endTime = searchParams.get('endTime');

    if (docketNo) urlParams.Docket_No = docketNo;
    if (contractNo) urlParams.Contract_No = contractNo;
    if (startTime) urlParams.startTime = startTime;
    if (endTime) urlParams.endTime = endTime;

    // 如果有 URL 參數，更新查詢條件
    if (Object.keys(urlParams).length > 0) {
      setQueryParams(prev => ({
        ...prev,
        ...urlParams,
        startTime: urlParams.startTime || prev.startTime,
        endTime: urlParams.endTime || prev.endTime
      }));

    }

    setUrlParamsProcessed(true);
  }, [searchParams, urlParamsProcessed]);

  const fetchSlumps = async () => {
    try {
      setLoading(true);

      // 使用核對出世紙專用API
      const [dataResponse, statisticsResponse] = await Promise.all([
        apiService.getBirthPaperData(queryParams),
        apiService.getBirthPaperStatistics(queryParams)
      ]);

      // 處理數據響應
      if (dataResponse) {
        setSlumps(dataResponse.data || []);
        setTotalCount(dataResponse.totalCount || 0);
      }

      // 處理統計信息響應
      if (statisticsResponse) {
        setStatistics({
          totalRecords: statisticsResponse.totalRecords || 0,
          unverifiedRecords: statisticsResponse.unverifiedRecords || 0,
          slumpTotal: statisticsResponse.slumpTotal || 0,
          concreteTotal: statisticsResponse.concreteTotal || 0,
          reportCount: statisticsResponse.reportCount || 0,
          totalBricks: statisticsResponse.totalBricks || 0,
          bricks28Day: statisticsResponse.bricks28Day || 0,
          bricksAB: statisticsResponse.bricksAB || 0,
          bricksEF: statisticsResponse.bricksEF || 0,
          bricksCD: statisticsResponse.bricksCD || 0,
          otherBricks: statisticsResponse.otherBricks || 0
        });
      }
    } catch (error) {
      console.error('Error fetching birth paper data:', error);
      showSnackbar(t('pages.siteSlump.messages.loadingError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 處理 Enter 鍵觸發搜索
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !loading) {
      event.preventDefault();
      fetchSlumps();
    }
  };


  // 處理分頁變化

  // 重置篩選條件（不自動查詢；清空結果直到再次按搜尋）
  const handleResetFilters = () => {
    const defaultParams: SiteSlumpBirthPaperQueryModel = {
      Docket_No: '',
      Contract_No: '',
      Cube_Mark: '',
      Time_Of_Water_Add: '',
      startTime: new Date().toISOString().split('T')[0],
      endTime: new Date().toISOString().split('T')[0],
    };
    setQueryParams(defaultParams);
    setSlumps([]);
    setTotalCount(0);
    setStatistics({
      totalRecords: 0,
      unverifiedRecords: 0,
      slumpTotal: 0,
      concreteTotal: 0,
      reportCount: 0,
      totalBricks: 0,
      bricks28Day: 0,
      bricksAB: 0,
      bricksEF: 0,
      bricksCD: 0,
      otherBricks: 0
    });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // 處理未核實記錄點擊
  const handleUnverifiedClick = () => {
    setUnverifiedDialog({ open: true });
  };

  // 關閉未核實記錄對話框
  const handleCloseUnverifiedDialog = () => {
    setUnverifiedDialog({ open: false });
  };

  // 處理導航到 Slump 頁面
  const handleNavigateToSlump = (docketNo: string) => {
    if (docketNo) {
      navigate(`/site-slump?Docket_No=${encodeURIComponent(docketNo)}&startTime=${encodeURIComponent(queryParams.startTime || '')}&endTime=${encodeURIComponent(queryParams.endTime || '')}`);
    }
  };

  // 導出搜索結果為Excel（使用後端API）
  const exportSearchResults = async () => {
    try {
      if (totalCount === 0) {
        showSnackbar(t('pages.siteSlump.messages.noDataToExport'), 'error');
        return;
      }

      // 調用後端API導出Excel
      const response = await apiService.exportSiteSlumpsExcel({
        queryParams: queryParams
      });

      // 創建Blob並下載
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // 從響應頭獲取文件名，如果沒有則使用默認名稱
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `Site_Slump_Export_${new Date().toISOString().split('T')[0]}.xlsx`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      // 下載文件
      saveAs(blob, fileName);
      showSnackbar(t('pages.siteSlump.messages.exportSuccess').replace('{count}', totalCount.toString()), 'success');
    } catch (error) {
      console.error('導出Excel失敗:', error);
      showSnackbar(t('pages.siteSlump.messages.exportError'), 'error');
    }
  };

  // 處理核實按鈕點擊
  const handleVerifySearchedData = () => {
    if (totalCount === 0) {
      showSnackbar('沒有可核實的記錄', 'error');
      return;
    }
    setVerifyDialog({ open: true, action: 'verify' });
  };

  // 確認批量核實
  const confirmBulkVerify = async () => {
    try {
      setLoading(true);
      const verifyValue = verifyDialog.action === 'verify' ? 'T' : 'F';

      // 轉換查詢參數格式以符合批量核實API要求
      // 注意：批量核實API使用SiteSlumpQueryModel，需要轉換格式
      const bulkVerifyQueryParams: any = {
        Docket_No: queryParams.Docket_No || undefined,
        Contract_No: queryParams.Contract_No || undefined,
        Cube_Mark: queryParams.Cube_Mark || undefined,
        Time_Of_Water_Add: queryParams.Time_Of_Water_Add || undefined,
        startTime: queryParams.startTime || '1970-01-01',
        endTime: queryParams.endTime || '1970-01-01',
      };

      // 調用批量核實API
      const response = await apiService.bulkVerifySiteSlumps({
        queryParams: bulkVerifyQueryParams,
        verifyValue: verifyValue
      });

      if (response.data && response.data.success) {
        showSnackbar(
          `成功${verifyDialog.action === 'verify' ? '核實' : '還原核實'} ${response.data.processedCount} 筆記錄`,
          'success'
        );
        // 刷新數據
        await fetchSlumps();
      } else {
        showSnackbar(
          `${verifyDialog.action === 'verify' ? '核實' : '還原核實'}失敗: ${response.data?.message || '未知錯誤'}`,
          'error'
        );
      }
    } catch (error: any) {
      console.error('批量核實失敗:', error);
      const errorMessage = error.response?.data?.message || error.message || '批量核實失敗';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
      setVerifyDialog({ open: false, action: 'verify' });
    }
  };

  return (
    <PageLayout title={t('pages.siteSlump.title')}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <BackButton sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 2, ml: 2 }} >{t('pages.siteSlump.slumpBirthPaper')}</Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 1 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('pages.siteSlump.fields.Docket_No')}
              value={queryParams.Docket_No}
              onChange={(e) => setQueryParams({ ...queryParams, Docket_No: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Autocomplete
              freeSolo
              options={contractNumbers}
              value={queryParams.Contract_No}
              onInputChange={(event, newInputValue) => {
                setQueryParams({ ...queryParams, Contract_No: newInputValue });
              }}
              onChange={(event, newValue) => {
                setQueryParams({ ...queryParams, Contract_No: newValue || '' });
              }}
              loading={loadingContracts}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label={t('pages.siteSlump.fields.Contract_No')}
                  onKeyDown={handleKeyDown}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingContracts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('pages.siteSlump.fields.Cube_Mark')}
              value={queryParams.Cube_Mark}
              onChange={(e) => setQueryParams({ ...queryParams, Cube_Mark: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('pages.siteSlump.fields.Time_Of_Water_Add')}
              value={queryParams.Time_Of_Water_Add}
              onChange={(e) => setQueryParams({ ...queryParams, Time_Of_Water_Add: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('pages.siteSlump.fields.testStartTime')}
              value={queryParams.startTime}
              onChange={(e) => setQueryParams({ ...queryParams, startTime: e.target.value, endTime: e.target.value })}
              onKeyDown={handleKeyDown}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('pages.siteSlump.fields.testEndTime')}
              value={queryParams.endTime}
              onChange={(e) => setQueryParams({ ...queryParams, endTime: e.target.value })}
              onKeyDown={handleKeyDown}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              onClick={fetchSlumps}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              {loading ? t('common.loading') : t('common.query')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleResetFilters}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              {t('common.reset')}
            </Button>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportSearchResults}
              disabled={totalCount === 0 || loading}
              sx={{
                mr: 1,
                borderColor: '#2196f3',
                color: '#2196f3',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#e3f2fd'
                }
              }}
            >
              {t('pages.siteSlump.messages.exportExcel')}
            </Button>
            {canVerify && slumps.length > 0 && (
              <Button
                variant="contained"
                startIcon={<VerifyIcon />}
                onClick={handleVerifySearchedData}
                disabled={totalCount === 0 || loading || !canVerify}
                sx={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#45a049'
                  }
                }}
              >
                核實已搜索的資料
              </Button>
            )}
          </Grid>
        </Grid>
        {/* 統計信息顯示 */}
        <Box sx={{
          mt: 2,
          p: 2,
          backgroundColor: 'grey.50',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                記錄總數目: <strong style={{ color: '#1976d2', fontSize: '1rem', fontWeight: 700 }}>{statistics.totalRecords}</strong>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  fontSize: '1rem',
                  mt: 1,
                  cursor: statistics.unverifiedRecords > 0 ? 'pointer' : 'default',
                  color: statistics.unverifiedRecords > 0 ? '#1976d2' : 'inherit',
                  textDecoration: statistics.unverifiedRecords > 0 ? 'underline' : 'none',
                  '&:hover': {
                    color: statistics.unverifiedRecords > 0 ? '#1565c0' : 'inherit'
                  }
                }}
                onClick={statistics.unverifiedRecords > 0 ? handleUnverifiedClick : undefined}
              >
                未核實記錄: <strong style={{ color: statistics.unverifiedRecords > 0 ? '#f44336' : '#4caf50', fontSize: '1rem', fontWeight: 700 }}>{statistics.unverifiedRecords}</strong> / <strong style={{ fontSize: '1rem', fontWeight: 700 }}>{statistics.totalRecords}</strong>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* 詳細資料顯示區域 - 只在搜索 Docket_No 和 Cube_Mark 時顯示 */}
      {slumps.length > 0 && (queryParams.Docket_No || queryParams.Cube_Mark || queryParams.Time_Of_Water_Add) && (() => {
        // 根據 Time_Water_Add 和 Mix_ID 分組
        const groupedSlumps = slumps.reduce((acc, slump) => {
          var key = `${slump.Time_Water_Add || ''}_${slump.Mix_ID || ''}`;

          if(slump.Remark?.toLowerCase().includes("retest")){
            key = `${key}_Retest_${slump.Report_ID}`;
          }

          if(slump.Remark?.toLowerCase().includes("reject")){
            key = `${key}_Reject_${slump.Report_ID}`;
          }

          if (!acc[key]) {
            acc[key] = [];
          }

          acc[key].push(slump);
          return acc;
        }, {} as Record<string, SiteSlump[]>);

        // 2. Convert to an Array and Sort it
        const sortedGroupedSlumps = Object.values(groupedSlumps).sort((groupA, groupB) => {
          // Grab the first item from each group to compare them
          const itemA = groupA[0];
          const itemB = groupB[0];

          // Create the "base" key to see if they belong to the same batch
          const baseKeyA = `${itemA.Time_Water_Add || ''}_${itemA.Mix_ID || ''}`;
          const baseKeyB = `${itemB.Time_Water_Add || ''}_${itemB.Mix_ID || ''}`;

          // PRIORITY 1: Group by Base Key
          // If they are different batches entirely, sort them alphabetically by their base key
          if (baseKeyA !== baseKeyB) {
            return baseKeyA.localeCompare(baseKeyB);
          }

          // PRIORITY 2: Sort by Finish Time
          const timeA = itemA.Time_Of_Test_Fin || ''; 
          const timeB = itemB.Time_Of_Test_Fin || '';

          // localeCompare safely compares strings. 
          // "16:18".localeCompare("16:23") will correctly put 16:18 first.
          return timeA.localeCompare(timeB);
        });

        return (
          <Box>
            {Object.entries(sortedGroupedSlumps).map(([key, group], groupIndex) => {
              const firstSlump = group[0]; // 使用第一條記錄作為代表顯示共同信息
              const getCompareDisplayValue = (
                selector: (item: SiteSlump) => string | number | null | undefined
              ) => {
                const normalizedValues = new Set(
                  group.map((item) => {
                    const value = selector(item);
                    return value === null || value === undefined ? '' : String(value).trim();
                  })
                );

                if (normalizedValues.size > 1) {
                  return 'Different Data Exist';
                }

                const firstValue = selector(firstSlump);
                return firstValue === null || firstValue === undefined || String(firstValue).trim() === ''
                  ? '-'
                  : String(firstValue);
              };

              const supplierDisplay = getCompareDisplayValue((item) => item.Supplier);
              const plantDisplay = getCompareDisplayValue((item) => item.Plant);
              const waterAddTimeDisplay = getCompareDisplayValue((item) => item.Time_Water_Add);
              const timeOnSiteDisplay = getCompareDisplayValue((item) => item.Time_On_Site);
              const samplingTimeDisplay = getCompareDisplayValue((item) => item.Time_Of_Sampling);
              const slumpTestFinishedDisplay = getCompareDisplayValue((item) => item.Time_Of_Test_Fin);
              const makingCubeTimeDisplay = getCompareDisplayValue((item) => item.Time_Of_Cube_Com);
              const gradeDisplay = getCompareDisplayValue((item) => item.Grade);
              const mixIdDisplay = getCompareDisplayValue((item) => item.Mix_ID);
              const designSlumpDisplay = getCompareDisplayValue((item) => item.Design_Slump);
              const measuredSlump1Display = getCompareDisplayValue((item) => item.Measured_Slump_1);
              const measuredSlump2Display = getCompareDisplayValue((item) => item.Measured_Slump_2);
              const photoNoDisplay = getCompareDisplayValue((item) => item.Photo_No);
              return (
                <Box key={key}>
                  {groupIndex > 0 && <Box sx={{ my: { xs: 2, sm: 2.5, md: 3 }, borderTop: '2px solid #000' }} />}

                  <Box
                    sx={{
                      p: { xs: 1.25, sm: 1.75, md: 2 },
                      mb: { xs: 2, md: 3 },
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    {/* Section 1: 左 Supplier／Plant；右側時間橫排；斷點越小間距越緊 */}
                    <Box sx={{ mb: { xs: 1.25, sm: 1.5, md: 2 } }}>
                      <Grid
                        container
                        alignItems="flex-start"
                        columnSpacing={{ xs: 1, sm: 2, md: 3, lg: 4 }}
                        rowSpacing={{ xs: 0, md: 0 }}
                      >
                        <Grid item xs={12} md={4} sx={{ pr: { md: 0.5, lg: 1.5 } }}>
                          <Typography
                            variant="body2"
                            sx={{ mb: 0.35, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700, color: '#000' }}
                          >
                            Supplier:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
                              color: '#4caf50',
                              letterSpacing: '0.02em',
                              wordBreak: 'break-word',
                              lineHeight: 1.3
                            }}
                          >
                            {supplierDisplay}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 0.35,
                              mt: { xs: 1.25, sm: 1.5, md: 2 },
                              fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                              fontWeight: 700,
                              color: '#000'
                            }}
                          >
                            Plant:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
                              color: '#4caf50',
                              letterSpacing: '0.02em',
                              wordBreak: 'break-word',
                              lineHeight: 1.3
                            }}
                          >
                            {plantDisplay}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={8} md={4} sx={{ pl: { md: 0.5, lg: 1.5 }, mt: { xs: 1.25, md: 0 } }}>
                          <Grid container columnSpacing={{ xs: 0.5, sm: 1, md: 1 }} rowSpacing={{ xs: 1, sm: 1.25 }}>
                            {[
                              { label: 'Water Add Time:', value: waterAddTimeDisplay },
                              { label: 'Time On Site:', value: timeOnSiteDisplay },
                              { label: 'Sample Time:', value: samplingTimeDisplay },
                              { label: 'Slump Test Finished:', value: slumpTestFinishedDisplay },
                              { label: 'Making Cube Time:', value: makingCubeTimeDisplay }
                            ].map(({ label, value }) => (
                              <Grid item xs={6} sm={4} md={2.4} key={label}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mb: 0.2,
                                    fontSize: { xs: '0.68rem', sm: '0.78rem', md: '0.85rem' },
                                    fontWeight: 700,
                                    color: '#000',
                                    lineHeight: 1.2
                                  }}
                                >
                                  {label}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '0.9rem', sm: '0.98rem', md: '1.05rem' },
                                    color: '#4caf50',
                                    letterSpacing: '0.02em',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.25
                                  }}
                                >
                                  {value || '-'}
                                </Typography>
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box sx={{ my: { xs: 1.25, sm: 1.5, md: 2 }, borderTop: '1px solid #000' }} />

                    {/* Section 2: Grade / MixID / Specified Slump */}
                    <Box sx={{ mb: { xs: 1.25, sm: 1.5, md: 2 } }}>
                      <Grid container rowSpacing={{ xs: 0.75, md: 0.5,sm: 0.75 }} columnSpacing={{ xs: 0, sm: 0.5, md: 0.5 }}>
                        <Grid item xs={12} md={2} sm={2}>
                          <Typography
                            variant="body2"
                            sx={{ mb: 0.35, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700, color: '#000' }}
                          >
                            Grade:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
                              color: '#4caf50',
                              letterSpacing: '0.02em',
                              wordBreak: 'break-word'
                            }}
                          >
                            {gradeDisplay}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={2} sm={4}>
                          <Typography
                            variant="body2"
                            sx={{ mb: 0.35, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700, color: '#000' }}
                          >
                            MixID:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
                              color: '#4caf50',
                              letterSpacing: '0.02em',
                              wordBreak: 'break-word'
                            }}
                          >
                            {mixIdDisplay}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={2} sm={4}>
                          <Typography
                            variant="body2"
                            sx={{ mb: 0.35, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700, color: '#000' }}
                          >
                            Specified Slump:
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
                              color: '#4caf50',
                              letterSpacing: '0.02em',
                              wordBreak: 'break-word'
                            }}
                          >
                            {designSlumpDisplay}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box sx={{ my: { xs: 1.25, sm: 1.5, md: 2 }, borderTop: '2px solid #000' }} />

                    {/* Section 3: Cube Mark / RFID 與 Slump 結果 */}
                    <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
                      <Grid container spacing={{ xs: 1.5, sm: 2, md: 1 }}>
                        <Grid item xs={12} md={6}>
                          <Grid container spacing={{ xs: 1.25, sm: 1.5, md: 2 }}>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: { xs: 0.75, sm: 1 },
                                  fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                                  fontWeight: 700,
                                  color: '#000'
                                }}
                              >
                                Cube Mark:
                              </Typography>
                              <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {group.map((slumpItem, itemIndex) => (
                                  <Box component="li" key={slumpItem.Report_ID || itemIndex} sx={{ mb: { xs: 0.5, sm: 0.75 } }}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                                        color: '#4caf50',
                                        letterSpacing: '0.02em',
                                        wordBreak: 'break-word',
                                        '&::before': {
                                          content: '"• "',
                                          color: '#4caf50',
                                          fontWeight: 600,
                                          marginRight: { xs: 0.5, sm: 1 }
                                        }
                                      }}
                                    >
                                      {slumpItem.Cube_Mark || '-'}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: { xs: 0.75, sm: 1 },
                                  fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                                  fontWeight: 700,
                                  color: '#000'
                                }}
                              >
                                RFID:
                              </Typography>
                              <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {group.map((slumpItem, itemIndex) => (
                                  <Box component="li" key={slumpItem.Report_ID || itemIndex} sx={{ mb: { xs: 0.5, sm: 0.75 } }}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.1rem' },
                                        color: '#4caf50',
                                        letterSpacing: '0.02em',
                                        wordBreak: 'break-all',
                                        '&::before': {
                                          content: '"• "',
                                          color: '#4caf50',
                                          fontWeight: 600,
                                          marginRight: { xs: 0.5, sm: 1 }
                                        }
                                      }}
                                    >
                                      {slumpItem.RFID || '-'}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box>
                            <Grid container spacing={0.5} rowSpacing={1}>
                              {/* Row 1 */}
                              <Grid item xs={5} md={5}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mb: { xs: 0.25, sm: 0.5 },
                                    fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                                    fontWeight: 700,
                                    color: '#000',
                                    lineHeight: 1.3
                                  }}
                                >
                                  Slump i{' '}
                                  {firstSlump.Slump_plate_No
                                    ? `(Plate ${firstSlump.Slump_plate_No.split(',')[0]?.trim() || '-'})`
                                    : '(Plate -)'}:
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '0.95rem', sm: '1.02rem', md: '1.08rem' },
                                    color: '#4caf50',
                                    letterSpacing: '0.02em',
                                    mb: 0.5
                                  }}
                                >
                                  {measuredSlump1Display}
                                </Typography>
                              </Grid>

                              <Grid item xs={5} md={5}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mb: { xs: 0.25, sm: 0.5 },
                                    fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                                    fontWeight: 700,
                                    color: '#000',
                                    lineHeight: 1.3
                                  }}
                                >
                                  Slump ii{' '}
                                  {firstSlump.Slump_plate_No
                                    ? `(Plate ${firstSlump.Slump_plate_No.split(',')[1]?.trim() || firstSlump.Slump_plate_No.split(',')[0]?.trim() || '-'})`
                                    : '(Plate -)'}:
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '0.95rem', sm: '1.02rem', md: '1.08rem' },
                                    color: '#4caf50',
                                    letterSpacing: '0.02em',
                                    mb: 0.5
                                  }}
                                >
                                  {measuredSlump2Display}
                                </Typography>
                              </Grid>

                              {/* Row 2 */}
                              <Grid item xs={5} md={5}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: { xs: 0.25, sm: 0.5 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700, color: '#000' }}
                                >
                                  Photo No:
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '0.95rem', sm: '1.02rem', md: '1.08rem' },
                                    color: '#4caf50',
                                    letterSpacing: '0.02em',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {photoNoDisplay}
                                </Typography>
                              </Grid>

                              <Grid item xs={5} md={5}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: { xs: 0.25, sm: 0.5 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700, color: '#000' }}
                                >
                                  Result of slump test:
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '0.95rem', sm: '1.02rem', md: '1.08rem' },
                                    color:
                                      firstSlump.Result_Of_Slump_Test === 'P'
                                        ? '#4caf50'
                                        : firstSlump.Result_Of_Slump_Test === 'F'
                                          ? '#f44336'
                                          : '#4caf50',
                                    letterSpacing: '0.02em'
                                  }}
                                >
                                  {firstSlump.Result_Of_Slump_Test || '-'}
                                </Typography>
                              </Grid>

                              {/* Row 3 */}
                              <Grid item xs={5} md={5}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: { xs: 0.25, sm: 0.5 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700, color: '#000' }}
                                >
                                  Conc Temp:
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '0.95rem', sm: '1.02rem', md: '1.08rem' },
                                    color: '#4caf50',
                                    letterSpacing: '0.02em',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {firstSlump.Conc_Temp || '-'}
                                </Typography>
                              </Grid>

                              <Grid item xs={5} md={5}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: { xs: 0.25, sm: 0.5 }, fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' }, fontWeight: 700, color: '#000' }}
                                >
                                  Form of slump:
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '0.95rem', sm: '1.02rem', md: '1.08rem' },
                                    color: '#4caf50',
                                    letterSpacing: '0.02em',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {(() => {
                                    const p1 = (firstSlump.Form_Of_Slump_1 || '').trim();
                                    const p2 = (firstSlump.Form_Of_Slump_2 || '').trim();
                                    const parts = [p1, p2].filter((p) => p.length > 0);
                                    return parts.length > 0 ? parts.join(',') : '-';
                                  })()}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      })()}

      {/* 統計信息卡片 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, border: '1px solid #e0e0e0', height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontSize: '1.3rem', fontWeight: 700 }}>
              基本統計
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500, minWidth: '140px', flexShrink: 0 }}>
                  Slump Total:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#4caf50', fontSize: '2.0rem', lineHeight: 1 }}>
                  {statistics.slumpTotal}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500, minWidth: '140px', flexShrink: 0 }}>
                  石矢Total:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#4caf50', fontSize: '2.0rem', lineHeight: 1 }}>
                  {statistics.concreteTotal}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500, minWidth: '140px', flexShrink: 0 }}>
                  Report數:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#4caf50', fontSize: '2.0rem', lineHeight: 1 }}>
                  {statistics.reportCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, border: '1px solid #e0e0e0', height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontSize: '1.3rem', fontWeight: 700 }}>
              磚塊統計
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500, minWidth: '140px', flexShrink: 0 }}>
                  總磚數:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#000000 ', fontSize: '3.0rem', lineHeight: 1 }}>
                  {statistics.totalBricks}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500, minWidth: '140px', flexShrink: 0 }}>
                  28Day磚:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#FF0000', fontSize: '2.0rem', lineHeight: 1 }}>
                  {statistics.bricks28Day}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500, minWidth: '140px', flexShrink: 0 }}>
                  AB磚:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#4caf50', fontSize: '2.0rem', lineHeight: 1 }}>
                  {statistics.bricksAB}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500, minWidth: '140px', flexShrink: 0 }}>
                  EF磚:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#4caf50', fontSize: '2.0rem', lineHeight: 1 }}>
                  {statistics.bricksEF}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500, minWidth: '140px', flexShrink: 0 }}>
                  CD磚:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#4caf50', fontSize: '2.0rem', lineHeight: 1 }}>
                  {statistics.bricksCD}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500, minWidth: '140px', flexShrink: 0 }}>
                  其他磚:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#000000 ', fontSize: '3.0rem', lineHeight: 1 }}>
                  {statistics.otherBricks}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* 未核實記錄對話框 */}
      <Dialog
        open={unverifiedDialog.open}
        onClose={handleCloseUnverifiedDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ color: '#f44336', fontWeight: 'bold' }}>
            未核實記錄詳情
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              共找到 {statistics.unverifiedRecords} 條未核實記錄
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {slumps
              .filter(slump => slump.Verify !== 'T')
              .map((slump, index) => (
                <ListItem
                  key={slump.Report_ID || index}
                  sx={{
                    px: 0,
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                  secondaryAction={
                    slump.Docket_No ? (
                      <IconButton
                        edge="end"
                        aria-label="前往 Slump 頁面"
                        onClick={() => handleNavigateToSlump(slump.Docket_No || '')}
                        color="primary"
                        sx={{
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'white'
                          }
                        }}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    ) : null
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        Docket No: {slump.Docket_No || 'N/A'}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          合約號碼: {slump.Contract_No || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          磚號: {slump.Cube_Mark || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          日期: {slump.Date ? new Date(slump.Date).toLocaleDateString('zh-HK') : 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          核實狀態:
                          <Chip
                            label={slump.Verify === 'T' ? '已核實' : '未核實'}
                            size="small"
                            color={slump.Verify === 'T' ? 'success' : 'error'}
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
          </List>
          {statistics.unverifiedRecords === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                沒有未核實的記錄
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnverifiedDialog} color="primary">
            關閉
          </Button>
        </DialogActions>
      </Dialog>

      {/* 核實確認對話框 */}
      <Dialog
        open={verifyDialog.open}
        onClose={() => setVerifyDialog({ open: false, action: 'verify' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {verifyDialog.action === 'verify' ? '確認核實' : '確認還原核實'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">
              {verifyDialog.action === 'verify'
                ? `您確定要核實所有符合搜索條件的記錄嗎？共 ${totalCount} 筆記錄將被核實。`
                : `您確定要還原核實所有符合搜索條件的記錄嗎？共 ${totalCount} 筆記錄將被還原核實。`}
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            此操作將影響所有符合當前搜索條件的記錄，請確認後再執行。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog({ open: false, action: 'verify' })}>
            取消
          </Button>
          <Button
            onClick={confirmBulkVerify}
            variant="contained"
            color={verifyDialog.action === 'verify' ? 'success' : 'warning'}
            disabled={loading}
          >
            {loading ? '處理中...' : verifyDialog.action === 'verify' ? '確認核實' : '確認還原核實'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default SlumpBirthPaperPage;